<template>
  <div class="step-content">
    <ul class="optional__navi">
      <li v-for="optionName in optionList"
        :key="optionName"
        :class="{on: optionName === currentOption}"
        @click="currentOption = optionName">
        {{ $t(`page.registration.optional.${optionName}.title`) }}
      </li>
    </ul>

    <!-- option 1 : report options -->
    <div v-show="currentOption === 'report'"
          class="option1 optional-content">

      <div class="option__pv-amount">
        <p class="label">{{ $t('page.registration.optional.report.pvLimit.label') }}</p>
        <ui-selectbox :value="internalPvLimit"
                      :items="pvLimitOptionList"
                      :width="275"
                      :disabled="isEditMode || isABPlan"
                      @input="onPVLimitChange($event)" />
        <p v-if="internalPvLimit === 0" class="custom-input--wrap">
          <input id="customPvLimit" type="number" min="1"
            v-model.number="customPvLimit"
            @blur="checkCustomPvLimit"> PV
        </p>
      </div>

      <div class="option__analysis-date">
        <p class="label">{{ $t('common.analysisPeriod') }}</p>
        <ul>

          <li v-if="disableStartDate">
            <span class="input__label">{{ $t('common.timezone') }}</span>
            <span>
              <span>
                {{timezoneName}}
              </span>
            </span>
          </li>

          <li>
            <span class="input__label">{{ $t('common.startDate') }}</span>
            <span v-if="disableStartDate">
              <span style="">{{ computedStartDateStrNoEdit }}</span>
              <span style="margin-left: 15px">{{ computedStartDateTimeNoEdit }}</span>
            </span>
            <template v-else>
              <label for="startDateInstantly">
                <input id="startDateInstantly" type="radio" name="startDate" value="true" :checked="!startDate"
                  @change="onDateOptionChange('startDate', false)"><i class="icon icon--radio-off"></i>
                <span>{{ $t('common.instantlyStart') }}</span>
              </label>
              <label for="startDateSelect">
                <input id="startDateSelect" type="radio" name="startDate" value="false" :checked="startDate"
                  @change="onDateOptionChange('startDate', true)"><i class="icon icon--radio-off"></i>
                <span>{{ $t('common.selectDate') }}</span>
                <div v-if="startDate" class="datepicker--wrap data__start-date">
                  <input type="text" readonly
                    :value="computedStartDateStr"
                    @click.stop.prevent="toggleDatePicker('start')">
                  <i class="icon icon--calendar-input"></i>
                  <date-picker v-if="showStartDatePicker"
                    :isShowTimezone="true"
                    :value="startDate"
                    :minDate="minStartDate"
                    :maxDate="maxStartDate"
                    :timezone="timezone"
                    :timezoneName="timezoneName"
                    @input="onCalendarDateChange('startDate', $event)"
                    @onCloseClick="showStartDatePicker = false"/>
                </div>
              </label>
            </template>
          </li>
          <li>
            <span class="input__label">{{ $t('common.endDate') }}</span>
            <label for="noEndDateTrue"
                   class="end-date-item" :class="{ disabled: disableEndDate }" v-if="userData.isGmc">
              <input id="noEndDateTrue" type="radio" name="endDate" value="true" :checked="!endDate"
                     :disabled="disableEndDate"
                     @change="onDateOptionChange('endDate', false)"><i class="icon icon--radio-off"></i>
              <span>{{ $t('common.nonStop') }}</span>
            </label>
            <label
              class="end-date-item" :class="{ disabled: disableEndDate }">
              <template v-if="userData.isGmc">
                <input id="noEndDateFalse" type="radio" name="endDate" value="false" :checked="endDate"
                       :disabled="disableEndDate"
                       @change="onDateOptionChange('endDate', true)"><i class="icon icon--radio-off"></i>
                <span>{{ $t('common.selectDate') }}</span>
              </template>
              <div v-if="endDate" class="datepicker--wrap data__end-date" :class="{'only-datepicker': !userData.isGmc}">
                <input type="text" readonly
                  :value="computedEndDateStr"
                  :disabled="disableEndDate"
                  @click.stop.prevent="toggleDatePicker('end')">
                <i class="icon icon--calendar-input"></i>
                <date-picker v-if="showEndDatePicker"
                  :isShowTimezone="true"
                  :value="endDate"
                  :minDate="minEndDate"
                  :timezone="timezone"
                  :timezoneName="timezoneName"
                  @input="onCalendarDateChange('endDate', $event)"
                  @onCloseClick="showEndDatePicker = false"/>
              </div>
            </label>
          </li>
        </ul>
      </div>
    </div>

    <!-- option 2 : capture options -->
    <div v-show="currentOption === 'capture'"
          class="option2 optional-content">
      <div>
        <p class="desc" v-html="$t('page.registration.optional.capture.desc')"></p>
        <input type="text"
               :placeholder="$t('common.placeHolder.enterExceptionSelector')"
               :value="exceptionSelector"
               @focus="onSetPageAddFocusContent(3, 2)"
               @input="$emit('update:exceptionSelector', $event.target.value.trim())">
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { format, parse, addDays, startOfDay, endOfDay } from 'date-fns'
import { dateToLocalTimezone, triggerClickEvent, utcToLocalTimezone } from '@/libs/utils'
import UiSelectbox from '@/components/ui/UiSelectbox'
import DatePicker from '@/components/date-picker'

const optionList = ['report', 'capture']
const DATE_FORAMT = 'YYYY-MM-DD HH:mm:ss'

export default {
  name: 'RegistrationStep3',
  components: {
    UiSelectbox,
    DatePicker
  },
  props: [
    'isEditMode',
    'pvLimit',
    'startDate',
    'endDate',
    'exceptionSelector',
    'editApplyRegDate',
    'timezone',
    'timezoneName',
    'editApplyMeta'
  ],
  data () {
    return {
      currentOption: optionList[0],
      internalPvLimit: null,
      customPvLimit: 3000,
      showStartDatePicker: false,
      showEndDatePicker: false,
      disableStartDate: false,
      disableEndDate: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'isABPlan'
    ]),
    selectedPVLimit () {
      return this.pvLimit
    },
    computedStartDate () {
      return utcToLocalTimezone(this.startDate || this.editApplyRegDate, this.timezoneName)
    },
    computedStartDateTime () {
      return format(this.startDate || this.editApplyRegDate, 'HH:mm:ss')
    },
    computedStartDateStr () {
      return format(this.startDate || this.editApplyRegDate, 'YYYY-MM-DD')
    },
    computedStartDateTimeNoEdit () {
      if (this.startDate) {
        return format(this.startDate, 'HH:mm:ss')
      } else {
        return format(utcToLocalTimezone(this.editApplyRegDate, this.timezoneName), 'HH:mm:ss')
      }
    },
    computedStartDateStrNoEdit () {
      if (this.startDate) {
        return format(this.startDate, 'YYYY-MM-DD')
      } else {
        return format(utcToLocalTimezone(this.editApplyRegDate, this.timezoneName), 'YYYY-MM-DD')
      }
    },
    computedEndDateStr () {
      return format(this.endDate, 'YYYY-MM-DD')
    },
    minStartDate () {
      return addDays(utcToLocalTimezone(new Date(), this.timezoneName), 1)
    },
    maxStartDate () {
      if (!this.endDate) {
        return null
      }
      return utcToLocalTimezone(parse(this.endDate), this.timezoneName)
    },
    minEndDate () {
      const now = utcToLocalTimezone(new Date(), this.timezoneName)
      if (this.startDate && parse(this.startDate) < parse(now)) {
        return now
      } else if (!this.startDate) {
        return now
      } else {
        return parse(this.startDate)
      }
    }
  },
  watch: {
    customPvLimit (value) {
      this.$emit('update:pvLimit', value)
    },
    startDate (value) {
      if (value && this.endDate && parse(value) > parse(this.endDate)) {
        this.$emit('update:endDate', utcToLocalTimezone(addDays(this.startDate, 6), this.timezoneName, DATE_FORAMT))
      }
      this.onSetPageAddFocusContent(3, 1)
    },
    pvLimit () {
      this.onSetPageAddFocusContent(3, 0)
    },
    endDate () {
      this.onSetPageAddFocusContent(3, 1)
    },
    currentOption (value) {
      if (value && value === 'capture') {
        this.onSetPageAddFocusContent(3, 2)
      }
    }
  },
  created () {
    this.optionList = optionList
    this.pvLimitOptionList = [
      {
        value: 3000,
        text: '3,000'
      },
      {
        value: 5000,
        text: '5,000'
      },
      {
        value: 10000,
        text: '10,000'
      },
      {
        value: 20000,
        text: '20,000'
      },
      {
        value: 50000,
        text: '50,000'
      },
      {
        value: 0,
        text: this.$t('page.registration.optional.report.pvLimit.directInput')
      },
      {
        value: -1,
        text: this.$t('common.noLimit')
      }
    ]

    const pvItem = this.pvLimitOptionList.find(item => {
      return item.value !== 0 && item.value === this.pvLimit
    })

    const pvLimit = pvItem ? pvItem.value : 0
    this.internalPvLimit = pvLimit

    if (this.internalPvLimit === 0) {
      this.customPvLimit = this.pvLimit
    }

    if (this.isEditMode) {
      const now = utcToLocalTimezone(new Date(), this.timezoneName)
      this.disableStartDate = !this.editApplyMeta.canChangeStartDate || !this.startDate || now >= parse(this.startDate)
      this.disableEndDate = !this.editApplyMeta.canChangeEndDate || (this.endDate && now > parse(this.endDate))
    }
  },
  methods: {
    ...mapMutations([
      'setPageAddGuideContent'
    ]),
    onSetPageAddFocusContent (step, index) {
      const values = { currentStep: step, currentContentIndex: index }
      this.setPageAddGuideContent(values)
    },
    onPVLimitChange (value) {
      this.internalPvLimit = value
      const pvLimit = this.internalPvLimit === 0 ? this.customPvLimit : value
      this.$emit('update:pvLimit', pvLimit)
    },
    onDateOptionChange (dateType, value) {
      let dateValue = null

      if (value) {
        const now = utcToLocalTimezone(new Date(), this.timezoneName)

        if (dateType === 'startDate') {
          dateValue = addDays(startOfDay(now), 1)
        } else {
          if (this.startDate && parse(this.startDate) > parse(now)) {
            dateValue = endOfDay(addDays(this.computedStartDateStr, 6))
          } else {
            dateValue = endOfDay(addDays(now, 6))
          }
        }
        dateValue = format(dateValue, DATE_FORAMT)
      }
      this.$emit(`update:${dateType}`, dateValue)
    },
    onCalendarDateChange (dateType, value) {
      let dateValue = parse(value)

      if (dateType === 'startDate') {
        dateValue = startOfDay(dateValue)
      } else {
        dateValue = endOfDay(dateValue)
      }
      dateValue = format(dateValue, DATE_FORAMT)
      this.$emit(`update:${dateType}`, dateValue)
    },
    formatDate (value) {
      return format(value, 'YYYY-MM-DD')
    },
    checkCustomPvLimit () {
      const value = Number(this.customPvLimit)

      if (!value || isNaN(value)) {
        this.customPvLimit = 3000
      }
    },
    toggleDatePicker (type) {
      switch (type) {
        case 'start':
          if (!this.showStartDatePicker) {
            triggerClickEvent()
          }
          this.showStartDatePicker = !this.showStartDatePicker
          break
        case 'end':
          if (!this.showEndDatePicker) {
            triggerClickEvent()
          }
          this.showEndDatePicker = !this.showEndDatePicker
          break
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.optional__navi {
  border-bottom: 1px solid #ebebeb;
  margin-top: 29px;
  padding-left: 35px;

  li {
    display: inline-block;
    min-width: 83px;
    height: 36px;
    padding: 11px 5px;
    box-sizing: border-box;
    text-align: center;

    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    color: #999999;
    cursor: pointer;

    &.on {
      border-bottom: 3px solid $brandColor;
      color: #2f2f2f;
    }
    &:nth-last-of-type(1) {
      margin-left: 30px;
    }
  }
}

.option1 {
  padding-top: 50px;
  padding-left: 140px;

  .option__pv-amount {
    .custom-input--wrap {
      display: inline-block;
      margin-left: 5px;
      vertical-align: top;
      #customPvLimit {
        border-radius: 6px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        width: 140px;
        height: 38px;
        padding-left: 19px;
        padding-right: 12px;
        text-align: right;
        box-sizing: border-box;
        font-size: 14px;

        &:focus {
          border-color: #289eef;
        }
      }
    }
  }

  .option__analysis-date {
    margin-top: 50px;
    ul {
      margin-top: 0;
      padding-left: 6px;
      li {
        display: flex;
        align-items: center;
        margin: 30px 0;

        .input__label {
          display: inline-block;
          width: 100px;
          vertical-align: middle;

          font-size: 13px;
          color: #767676;
        }

        label {
          position: relative;
          display: inline-block;
          white-space: nowrap;

          &:nth-of-type(1) {
            margin-right: 40px;
          }

          & > .icon:not(.icon--calendar-input){
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
          }

          & > span {
            display: inline-block;
            margin-left: 26px;
            font-size: 14px;
            color: #2f2f2f;
          }

          &.end-date-item {
            &.disabled {
              opacity: 0.5;
              pointer-events: none;
            }
          }
        }

        &.timezone--wrap {
          margin-top: 10px;
          margin-bottom: -10px;
          .timezone-select {
            vertical-align: middle;
          }
        }

        .datepicker--wrap {
          @include positionYcenter;
          display: inline-block;
          margin-left: 15px;

          & > .icon {
            position: absolute;
            top: 50%;
            left: 9px;
            transform: translateY(-50%);
          }

          & > input[type=text] {
            width: 129px;
            height: 38px;
            border-radius: 6px;
            background-color: #ffffff;
            border: 1px solid #bbbbbb;
            box-sizing: border-box;
            text-align: right;
            font-size: 14px;
            color: #2f2f2f;
            padding-right: 12px;

            &:focus {
              border-color: #289eef;
            }
          }

          .date_picker {
            top: -297px;
            left: 50%;
          }
        }
      }
    }
  }
}

.option2 {
  padding-top: 68px;
  padding-left: 70px;

  .desc {
    font-size: 13px;
    font-weight: bold;
    line-height: 1.54;
    color: #767676;
  }

  input {
    margin-top: 12px;
    width: 596px;
    height: 38px;
    border-radius: 6px;
    border: solid 1px #bbbbbb;
    padding-left: 12px;
    padding-right: 12px;
    box-sizing: border-box;
    background-color: #ffffff;
    font-size: 14px;

    &:focus {
      border-color: #289eef;
    }

    &::placeholder {
      color: #bbb;
    }
  }
}
html[lang="ja"] {
  .option1 .option__analysis-date ul li label {
    width: 100px;
  }
}
html[lang="en"] {
  .option1 .option__analysis-date ul li label {
    width: 125px;
  }
}
</style>
